<template>
  <li
    :class="{
      'calendar-input-snippet': true,
      'calendar-input-snippet--disabled': props.isDisabled,
    }"
  >
    <!-- Edit state -->
    <div v-if="isEditMode" class="edit-state calendar-input-snippet__edit-state">
      <div class="edit-state__text-inputs">
        <input class="global-text-input edit-state__date-input" type="text" placeholder="Date" v-model="calendarInputDate" />

        <div class="edit-state__title-and-description-inputs">
          <input class="global-text-input edit-state__title-input" type="text" placeholder="Title" v-model="calendarInputTitle" />

          <input class="global-text-input edit-state__description-input" type="text" placeholder="Description" v-model="calendarInputDescription" />

          <div class="global-select-wrap" style="max-width: 150px">
            <select class="global-select" v-model="calendarInputSource">
              <option value="template">General event</option>
              <option value="local">Local event</option>
            </select>
            <div class="global-select-wrap__arrow">&nbsp;</div>
          </div>
        </div>
      </div>

      <div class="edit-state__buttons-wrap">
        <SrpButton
          class="edit-state__button"
          @click="
            () => {
              isEditMode = false;
              copyValuesToLocalVars();
            }
          "
          color="gray"
          fill="outlined"
        >
          Cancel
        </SrpButton>

        <SrpButton class="edit-state__button" @click="save">
          <template #icon><IconEmbedded name="save_2" :size="20" /></template>
          Save
        </SrpButton>
      </div>
    </div>
    <!-- / Edit state -->

    <!-- View state -->
    <div v-else class="view-state calendar-input-snippet__view-state">
      <!-- Date snippet -->
      <div
        :class="{
          'date-snippet': true,
          'date-snippet--disabled': isDeleting,
          'view-state__date-snippet': true,
        }"
      >
        <IconEmbedded class="date-snippet__calendar-icon" name="calendar_2" :size="20" color="rgba(0, 0, 0, 0.5)" />

        <div class="date-snippet__text">
          {{ calendarInputDate }}
        </div>
      </div>
      <!-- / Date snippet -->

      <div class="view-state__title-and-description">
        <h2
          :class="{
            'global-h2': true,
            'view-state__title': true,
            'view-state__title--disabled': isDeleting,
          }"
        >
          {{ calendarInputTitle }}
        </h2>

        <div
          :class="{
            'view-state__description': true,
            'view-state__description--disabled': isDeleting,
          }"
        >
          {{ calendarInputDescription }}
        </div>

        <div v-if="props.showUrl && props.calendarInput.url" class="view-state__url">
          <b>Source:</b> <a :href="props.calendarInput.url" target="_blank" rel="noopener noreferrer">{{ props.calendarInput.url }}</a>
        </div>
      </div>

      <div class="view-state__buttons-wrap">
        <CircleWithIconBtn
          :class="{
            'view-state__button': true,
            'view-state__button--disabled': isDeleting,
          }"
          iconName="drag-n-drop-handle-3-lines"
          color="white"
          size="big"
          :iconSize="23"
          style="cursor: grab"
          data-drag-n-drop-handle
          :data-id="props.calendarInput.id"
          :data-index="props.index"
          :data-source="props.calendarInput.source"
        />

        <CircleWithIconBtn
          :class="{
            'view-state__button': true,
            'view-state__button--disabled': isDeleting,
          }"
          iconName="pencil_2"
          color="white"
          size="big"
          :iconSize="17"
          @click="isEditMode = true"
        />

        <CircleWithIconBtn v-if="isDeleting" class="view-state__button" iconName="trashcan-alt_2" color="white" :iconSize="18" size="big">
          <LoadingIcon :size="17" />
        </CircleWithIconBtn>

        <CircleWithIconBtn v-else class="view-state__button" iconName="trashcan-alt_2" color="white" :iconSize="18" size="big" @click="deleteInput" />
      </div>
    </div>
    <!-- / View state -->
  </li>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";

// Types
import { ContentCalendarInput } from "@contracts/contentCalendar";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CircleWithIconBtn from "@components/ui/CircleWithIconBtn.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

const props = withDefaults(
  defineProps<{
    calendarInput: ContentCalendarInput;
    customerId: string;
    showUrl?: boolean;
    isDisabled?: boolean;
    index?: number;
  }>(),
  {
    isDisabled: false,
    index: -1,
  }
);

const emit = defineEmits<{
  (e: "editModeSwitched", isEditMode: boolean): void;
  (e: "saved", date: string, title: string, description: string, source: string): void;
  (e: "deleted", id: string): void;
}>();

// Switch Edit mode ===========================================================
const isEditMode = ref<boolean>(false);

watch(isEditMode, () => emit("editModeSwitched", isEditMode.value));

// Local date/title/description values ========================================
const calendarInputDate = ref<string>("");
const calendarInputTitle = ref<string>("");
const calendarInputDescription = ref<string>("");
const calendarInputSource = ref<"template" | "local">("");

onMounted(copyValuesToLocalVars);

function copyValuesToLocalVars() {
  calendarInputDate.value = props.calendarInput.date;
  calendarInputTitle.value = props.calendarInput.title;
  calendarInputDescription.value = props.calendarInput.description;
  calendarInputSource.value = props.calendarInput.source;
}

// Save =======================================================================
async function save() {
  isEditMode.value = false;

  await ContentCalendarRepo.updateInput(props.customerId, props.calendarInput.id, {
    ...props.calendarInput,
    date: calendarInputDate.value,
    title: calendarInputTitle.value,
    description: calendarInputDescription.value,
    source: calendarInputSource.value,
  });

  emit("saved", calendarInputDate.value, calendarInputTitle.value, calendarInputDescription.value, calendarInputSource.value);
}

// Delete =====================================================================
const isDeleting = ref<boolean>(false);

async function deleteInput() {
  isDeleting.value = true;
  await ContentCalendarRepo.deleteInput(props.customerId, props.calendarInput.id);
  isDeleting.value = false;

  emit("deleted", props.calendarInput.id);
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
@import "@/scss/screen-size-ranges.scss";

// Date snippet ===============================================================
.date-snippet {
  @include standardFont;
  display: flex;
  align-items: flex-start;

  &--disabled {
    opacity: 0.5;
  }

  &__calendar-icon {
    margin: -3px 4px 0 0;
  }

  &__text {
    line-height: 15px;
  }
}

// View state =================================================================
.view-state {
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 0;

  &__date-snippet {
    width: 130px;
    margin: 5px 20px 0 0;
  }

  &__title-and-description {
    flex-grow: 1;
    margin-right: 85px;
    display: flex;
    flex-direction: column;
  }

  &__title {
    margin-bottom: 6px;
    font-weight: 600;
    line-height: 22px;

    &--disabled {
      opacity: 0.5;
    }
  }

  &__description {
    @include standardFont;
    line-height: 15px;

    &--disabled {
      opacity: 0.5;
    }
  }

  &__buttons-wrap {
    gap: 8px;
    display: flex;
    align-items: center;
    position: absolute;
    inset: -3px -7px auto auto;
  }

  &__button {
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__url {
    margin-top: 6px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .view-state {
    flex-wrap: wrap;
    align-content: center;

    &__date-snippet {
      width: auto;
      margin: 5px 0 10px 0;
    }

    &__title-and-description {
      order: 3;
      width: 100%;
      min-width: 100%;
      margin: 0;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .view-state {
    flex-wrap: wrap;
    align-content: center;

    &__date-snippet {
      width: auto;
      margin: 5px 0 10px 0;
    }

    &__title-and-description {
      order: 3;
      width: 100%;
      min-width: 100%;
      margin: 0;
    }
  }
}

// Edit state =================================================================
.edit-state {
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 0;

  &__text-inputs {
    flex-grow: 1;
    gap: 15px;
    margin-right: 45px;
    display: flex;
    align-items: flex-start;
  }

  &__date-input {
    width: 130px;
    margin-right: 10px;
  }

  &__title-and-description-inputs {
    flex-grow: 1;
    gap: 7px;
    display: flex;
    flex-direction: column;
  }

  &__title-input {
    font-weight: bold;
  }

  &__description-input {
  }

  &__buttons-wrap {
    gap: 10px;
    display: flex;
  }

  &__button {
    display: flex;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .edit-state {
    flex-direction: column;

    &__date-input {
      width: calc(50% - 30px);
    }

    &__text-inputs {
      width: 100%;
      margin: 0 0 20px;
      flex-direction: column;
    }

    &__title-and-description-inputs {
      width: 100%;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .edit-state {
    flex-direction: column;

    &__date-input {
      width: 50%;
    }

    &__text-inputs {
      width: 100%;
      margin: 0 0 20px;
      flex-direction: column;
    }

    &__title-and-description-inputs {
      width: 100%;
    }
  }
}

// Calendar input snippet =====================================================
.calendar-input-snippet {
  padding: 14px 0 18px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 0;
  background: #f3f3f3;

  &--disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  &[data-cloned-element-for-photos-section-mouse-drag-event="true"]::before {
    display: none;
  }

  &__view-state {
    width: 100%;
    margin: 0 19px;
  }

  &__edit-state {
    width: 100%;
    margin: 0 12px;
  }
}
</style>
